import { CreateSalesOrderWithItemsDto, CreateSalesOrderPaymentDto } from '../../api';
import type {
  OrderSummary,
  PaymentConfig,
  SalesModalState,
  SelectedProduct,
} from '../../types/sales.types';
import { roundMoney, clampMoney } from '../money';
import { PAYMENT_METHODS } from '../constants/paymentMethods';
import { commercialDateOverrideIso } from './commercial-sale-date';

type CreateOrderPaymentDto = Omit<CreateSalesOrderPaymentDto, 'salesOrderId'>;

export interface BuildCreateOrderPayloadInput {
  state: Pick<
    SalesModalState,
    | 'selectedLocation'
    | 'selectedCustomer'
    | 'selectedSeller'
    | 'selectedProducts'
    | 'discountConfig'
    | 'paymentConfig'
    | 'saleDate'
    | 'saleDateMode'
    | 'orderCharges'
    | 'hasInvoice'
  >;
  orderSummary: OrderSummary;
  currentUserId?: string;
  getDiscountType: (
    discountType: string,
  ) => import('../../api').CreateSalesOrderItemDto.discountType;
  getMarkupType: (markupType: string) => import('../../api').CreateSalesOrderItemDto.markupType;
  /** Required by API when any line finalPrice is 0. */
  confirmZeroSale?: boolean;
}

function mapPayments(paymentConfig: PaymentConfig): CreateOrderPaymentDto[] {
  return paymentConfig.payments.map((payment) => {
    const paymentDto: Record<string, unknown> = {
      paymentAmount: roundMoney(payment.amount),
      paymentMethod: payment.method,
      paymentType: CreateSalesOrderPaymentDto.paymentType.ORDER,
    };

    const trimmedNotes = payment.paymentNotes?.trim();
    if (trimmedNotes != null && trimmedNotes !== '') {
      paymentDto.paymentNotes = trimmedNotes;
    }

    if (
      payment.method === PAYMENT_METHODS.CASH &&
      payment.cashReceived != null &&
      payment.cashReceived > payment.amount
    ) {
      paymentDto.changeGiven = roundMoney(payment.cashReceived - payment.amount);
    }

    if (payment.method === PAYMENT_METHODS.QR && payment.qrTransactionId) {
      paymentDto.qrTransactionId = payment.qrTransactionId;
    } else if (payment.method === PAYMENT_METHODS.QR) {
      paymentDto.qrTransactionId = `QR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    if (payment.method === PAYMENT_METHODS.CARD && payment.bankReference) {
      paymentDto.bankReference = payment.bankReference;
    } else if (payment.method === PAYMENT_METHODS.CARD) {
      paymentDto.bankReference = `CARD-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }

    return paymentDto as CreateOrderPaymentDto;
  });
}

function mapItems(
  products: SelectedProduct[],
  getDiscountType: BuildCreateOrderPayloadInput['getDiscountType'],
  getMarkupType: BuildCreateOrderPayloadInput['getMarkupType'],
) {
  return products.map((product) => ({
    warehouseInventoryProductId: product.warehouseInventoryProduct.id,
    originalPrice: roundMoney(product.originalPrice),
    markupType: getMarkupType(product.markupType),
    markupValue: roundMoney(product.markupValue || 0),
    adjustedPrice: roundMoney(product.adjustedPrice),
    discountType: getDiscountType(product.discountType),
    discountValue: roundMoney(product.discountValue || 0),
    finalPrice: roundMoney(product.finalPrice),
    itemDiscountApplied: product.discountValue > 0 || product.markupValue > 0,
    isDelivered: Boolean(product.isDelivered),
  }));
}

export function buildCreateOrderPayload({
  state,
  orderSummary,
  currentUserId,
  getDiscountType,
  getMarkupType,
  confirmZeroSale = false,
}: BuildCreateOrderPayloadInput): CreateSalesOrderWithItemsDto {
  if (currentUserId == null || currentUserId.trim() === '') {
    throw new Error('Sesión inválida: no se pudo identificar al usuario. Vuelve a iniciar sesión.');
  }
  const userId = currentUserId;
  const subtotal = roundMoney(orderSummary.subtotal);
  const discountAmount = roundMoney(orderSummary.totalDiscount);
  const extraChargesTotal = roundMoney(orderSummary.totalCharges);
  const subtotalBeforeTax = roundMoney(orderSummary.grandTotal);
  const totalAmount = roundMoney(orderSummary.grandTotal);
  const hasExtraCharges = extraChargesTotal > 0;

  const totalCost = state.selectedProducts.reduce((sum, product) => {
    return sum + (product.warehouseInventoryProduct.costPrice || 0);
  }, 0);
  const validTotalCost = clampMoney(totalCost);

  const receiptNumber = `REC-${Date.now()}`;
  const saleDateOverride = commercialDateOverrideIso(state.saleDateMode, state.saleDate);

  return {
    locationId: state.selectedLocation?.id || 1,
    createdById: state.selectedSeller?.id ?? userId,
    customerId: state.selectedCustomer?.id,
    status: CreateSalesOrderWithItemsDto.status.CONFIRMED,
    salesChannel: CreateSalesOrderWithItemsDto.salesChannel.IN_STORE,
    priorityLevel: CreateSalesOrderWithItemsDto.priorityLevel.NORMAL,
    hasDiscount: discountAmount > 0,
    discountScope:
      state.discountConfig.enabled &&
      !state.discountConfig.allowIndividualDiscounts &&
      state.discountConfig.orderDiscountType === 'AMOUNT'
        ? CreateSalesOrderWithItemsDto.discountScope.PER_ORDER
        : CreateSalesOrderWithItemsDto.discountScope.PER_ITEM,
    hasPartialPayment: orderSummary.paymentStatus === 'partial',
    partialPaymentScope: CreateSalesOrderWithItemsDto.partialPaymentScope.PER_ORDER,
    hasInstallments: false,
    installmentsFrequency: undefined,
    subtotal: roundMoney(subtotal),
    discountAmount: roundMoney(discountAmount),
    subtotalBeforeTax: roundMoney(subtotalBeforeTax),
    totalWithTax: roundMoney(totalAmount),
    totalAmount: roundMoney(totalAmount),
    extraChargesTotal: roundMoney(extraChargesTotal),
    hasExtraCharges,
    hasInvoice: state.hasInvoice,
    ...(confirmZeroSale === true ? { confirmZeroSale: true } : {}),
    totalCost: roundMoney(validTotalCost),
    profitMargin:
      totalAmount > 0
        ? Number((((totalAmount - validTotalCost) / totalAmount) * 100).toFixed(2))
        : 0,
    orderNotes: `Venta realizada en ${state.selectedLocation?.name || 'Sucursal'}`,
    customerNotes: undefined,
    estimatedCompletion: new Date().toISOString(),
    lastPaymentDate: new Date().toISOString(),
    nextPaymentDue:
      orderSummary.paymentStatus === 'partial'
        ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
        : undefined,
    ...(saleDateOverride != null ? { saleDate: saleDateOverride } : {}),
    items: mapItems(state.selectedProducts, getDiscountType, getMarkupType),
    charges: state.orderCharges.map((charge) => ({
      categoryCode: charge.categoryCode,
      amount: roundMoney(charge.amount),
      description: charge.description?.trim() || undefined,
    })),
    payments: mapPayments(state.paymentConfig) as any,
    receipt: {
      receiptNumber,
      customerName: state.selectedCustomer?.customerName || 'Cliente General',
      customerEmail: state.selectedCustomer?.customerEmail,
      customerPhone: state.selectedCustomer?.customerPhone,
      sendReceiptEmail: !!state.selectedCustomer?.customerEmail,
    },
  };
}
